import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';

export default function UserNotifications() {
  const { formatDate, t } = useI18n();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.getUserNotifications()
      .then((res) => {
        if (mounted) setNotifications(res.data || []);
      })
      .catch(() => {
        if (mounted) setError(t('alerts.error'));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [t]);

  async function markRead(notification) {
    if (notification.read_at) return;
    setNotifications((current) => current.map((item) => (
      item.id === notification.id ? { ...item, read_at: new Date().toISOString() } : item
    )));
    try {
      await api.markUserNotificationRead(notification.id);
      window.dispatchEvent(new Event('od:notifications-refresh'));
    } catch {
      // Read state is cosmetic; the next load will reconcile it.
    }
  }

  function renderNotificationCopy(notification) {
    if (notification.type === 'profile_incomplete') {
      const missing = notification.payload?.missing || [];
      const hasLocation = missing.includes('location');
      const hasStyleGender = missing.includes('style_gender');
      const bodyKey = hasLocation && hasStyleGender
        ? 'alerts.profileIncomplete.bodyBoth'
        : hasLocation
          ? 'alerts.profileIncomplete.bodyLocation'
          : 'alerts.profileIncomplete.bodyStyleGender';

      return {
        title: t('alerts.profileIncomplete.title'),
        body: t(bodyKey),
      };
    }

    if (notification.type !== 'followed_profile_admin_action') {
      return {
        title: notification.title,
        body: notification.body,
      };
    }

    const payload = notification.payload || {};
    const profile = payload.profile_name || t('alerts.followedProfile.fallbackName');
    const actionKey = payload.action === 'banned'
      ? 'banned'
      : payload.action === 'deleted_banned'
        ? 'deletedBanned'
        : 'removed';
    const bodyKey = payload.reason
      ? `alerts.followedProfile.${actionKey}.bodyReason`
      : `alerts.followedProfile.${actionKey}.body`;

    return {
      title: t(`alerts.followedProfile.${actionKey}.title`),
      body: t(bodyKey, { profile, reason: payload.reason }),
    };
  }

  return (
    <section className="ct-alerts-page">
      <div className="ct-account-panel-head">
        <i className="ph ph-bell-ringing"></i>
        <div>
          <strong>{t('alerts.title')}</strong>
          <p>{t('alerts.copy')}</p>
        </div>
      </div>

      {loading ? <p className="ct-muted">{t('common.loading')}</p> : null}
      {error ? <p className="ct-account-delete-error">{error}</p> : null}

      {!loading && notifications.length === 0 ? (
        <div className="ct-account-panel ct-alert-empty">
          <i className="ph ph-check-circle"></i>
          <strong>{t('alerts.emptyTitle')}</strong>
          <p>{t('alerts.emptyCopy')}</p>
        </div>
      ) : null}

      <div className="ct-alert-list">
        {notifications.map((notification) => {
          const copy = renderNotificationCopy(notification);

          return (
            <article className={`ct-alert-item ${notification.read_at ? '' : 'unread'}`} key={notification.id}>
              <span className="ct-alert-icon">
                <i className="ph ph-info"></i>
              </span>
              <div>
                <p className="ct-kicker">
                  {notification.created_at ? formatDate(notification.created_at, { dateStyle: 'medium', timeStyle: 'short' }) : t('alerts.notice')}
                </p>
              <h2>{copy.title}</h2>
              <p>{copy.body}</p>
              {notification.email_sent_at ? (
                <small>{t('alerts.emailSent', { date: formatDate(notification.email_sent_at, { dateStyle: 'medium', timeStyle: 'short' }) })}</small>
              ) : null}
            </div>
            {notification.type === 'profile_incomplete' ? (
              <Link to="/profile?tab=overview">
                {t('alerts.profileIncomplete.cta')}
              </Link>
            ) : null}
            {!notification.read_at && notification.type !== 'profile_incomplete' ? (
              <button type="button" onClick={() => markRead(notification)}>
                {t('alerts.markRead')}
              </button>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}
